import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import VideoBox from "../../casinoComponents/videoBox";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import { getCasinoGameDetails, getMatchExposure, getCasinoOpenBets } from "../../../apiservices/CasionApi";
import ViewMoreModal from "../../../components/ViewMoreModal";
import { extractCasinoGame } from "../../../utils/casinoGame";
import WarCardsOverlay from "../../casinoComponents/videoBox/overlays/WarCardsOverlay";
import BetTableWar from "./BetTableWar";
import WarRefResults from "./WarRefResults";
import "../../../styles/games/casinowar.css";

// Reference game-header title (lastresults2 res1.cname)
const WAR_TITLE = "Casino War";

/**
 * Casino War (casino/war) in the reference shell: .casino-table.casino-war.detail-page-container >
 *   .game-header + .casino-video (dealer card overlay) + .casino-detail (6-player grid, remark, Last Result).
 */
export default function CasinoWar() {
  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname);
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;

  useEffect(() => {
    const gameName = extractCasinoGame(pathname);
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error("API Fetch Error:", err.message);
        setGameData(null); // clear data on error
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  const handleBetClick = (value) => {
    if (!value) return;
    fetchExposure();
    fetchMyBetsData();
  };

  const matchId = gameData?.data?.data?.mid?.toString();
  const subData = Array.isArray(gameData?.data?.data?.sub) ? gameData.data.data.sub : [];

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [matchId]);

  // My Bets: same API as lord-admin – GET /lords/bet-list/casino/open-downline?gameName=...
  const fetchMyBetsData = useCallback(async () => {
    const gameName = extractCasinoGame(pathname);
    if (!gameName) return;
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [pathname]);

  // Fetch exposure and My Bets on mount and periodically (My Bets every 5s like lord-admin)
  useEffect(() => {
    if (matchId) {
      const t = setTimeout(() => {
        fetchExposure();
      }, 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, matchId]);

  useEffect(() => {
    fetchMyBetsData();
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => clearInterval(intervalId);
  }, [fetchMyBetsData]);

  // Last result: same as lord-admin — from getCasinoGameDetails response (lrs) or empty
  const resultData = gameData?.lrs ?? gameData?.data?.data?.lrs ?? gameData?.data?.lrs ?? [];

  const feed = gameData?.data?.data ?? {};
  const roundId = feed?.mid || gameData?.data?.mid || "";
  const cards = feed?.card != null && String(feed.card).length ? String(feed.card).split(",").map((c) => c.trim()) : [];

  // Admin places no bets: an odds click refreshes book + bets (reference click opens the bet slip)
  const onOdds = (m) => {
    if (!m || String(m.gstatus || "").toUpperCase() !== "OPEN") return;
    handleBetClick(m.b, m.nat, "back", m);
  };

  return (
    <CasinoTableLayout
      tableClass="casino-war detail-page-container"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading name={WAR_TITLE} roundId={roundId} />{" "}
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={feed?.lt}
          gameType={gameName}
          cardsOverlay={cards.length ? <WarCardsOverlay card={feed.card} /> : null}
        />{" "}
        {feed?.mid ? (
          <div className="casino-detail">
            <BetTableWar sub={subData} cards={cards} exposures={exposures} onOdds={onOdds} />{" "}
            {feed?.remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} alt="" />
                </div>{" "}
                <marquee>{feed.remark}</marquee>
              </div>
            ) : null}{" "}
            <CasinoLastResult
              gameType={gameName}
              data={resultData}
              gameData={gameData}
              viewAllLink={`/admin/reports/casinoresult/${gameName}`}
              resultComponent={<WarRefResults gameType={gameName} mid={feed?.mid} fallbackTitle={WAR_TITLE} />}
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
      />
    </CasinoTableLayout>
  );
}
